'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArtigoLista } from '@/lib/types';

// Cache local simples com TTL para evitar chamadas repetidas
let ARTICLES_LIST_CACHE: { data: ArtigoLista[]; timestamp: number } | null = null;
const ARTICLES_LIST_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutos

const AREAS = [
  { id: '0', color: '#3C6E71', title: 'Direito Cível' },
  { id: '1', color: '#2EC4B6', title: 'Direito Imobiliário' },
  { id: '2', color: '#5F6CAF', title: 'Assessoria Jurídica' },
  { id: '3', color: '#F4A261', title: 'Direito Público e Administrativo' },
  { id: '4', color: '#F4A261', title: 'Direito Público' },
  { id: '5', color: '#F4A261', title: 'Direito Administrativo' },
  { id: '6', color: '#F28482', title: 'Direito de Família' },
  { id: '7', color: '#E391ED', title: 'Direito Tributário' },
];

function getAreaColor(tag: string) {
  const area = AREAS.find((a) => a.title === tag);
  return area ? area.color : '#6B7280';
}

function formatarData(data: string) {
  return new Date(data).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function ArticlesList() {
  const [artigos, setArtigos] = useState<ArtigoLista[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchArticles = async () => {
    try {
      const now = Date.now();
      if (
        ARTICLES_LIST_CACHE &&
        now - ARTICLES_LIST_CACHE.timestamp < ARTICLES_LIST_CACHE_TTL_MS
      ) {
        setArtigos(ARTICLES_LIST_CACHE.data);
        return;
      }

      const res = await fetch('/api/artigos');
      if (!res.ok) {
        const msg = `Falha na API: ${res.status} ${res.statusText}`;
        throw new Error(msg);
      }
      const data: ArtigoLista[] = await res.json();
      setArtigos(data);
      ARTICLES_LIST_CACHE = { data, timestamp: Date.now() };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setArtigos([]);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  if (artigos.length === 0 || error) {
    return null;
  }

  const artigosOrdenados = [...artigos].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 gap-4 sm:gap-4">
        {artigosOrdenados.map((artigo) => (
          <Link
            key={artigo.slug}
            href={`/artigo/${artigo.slug}`}
            className="group bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300"
          >
            <article className="h-full flex flex-col">
              <div className="p-4 sm:p-6 flex flex-col h-full">
                <header>
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                    <p className="text-sm text-gray-500">{formatarData(artigo.date)}</p>
                    {artigo.tags && artigo.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {artigo.tags.map((tag) => (
                          <span
                            key={tag}
                            style={{ backgroundColor: getAreaColor(tag) }}
                            className="px-2 py-1 text-white text-xs font-bold rounded-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <h2 className="text-primary text-base sm:text-2xl font-bold mb-2">{artigo.title}</h2>
                </header>

                <div className="pt-2 border-t border-gray-100">
                  <span className="text-secondary sm:text-primary text-sm font-bold group-hover:text-secondary">
                    Ler mais →
                  </span>
                </div>
              </div>
            </article>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default ArticlesList;
